import { Component, ChangeDetectionStrategy, Input } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '@core/services/api/auth.service';
import { ConfirmDialogConfig } from '@models/dialog';
import { ModalService } from '@core/services/modal.service';
import { INavigation } from '@models/navigation';
import { IPermissionCheck, PermissionCheck } from '@models/role';
import { ROUTES } from '@utils/constants';
import { IUserDTO, UserDTO } from '@models/user';
import { SaveCancelButtonConfig } from '@models/form/button';
import { switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { StudentCritterService } from '@app/infrastructure/core/services/api/student-critter.service';
import { Critter } from '@app/infrastructure/models/critter';
import { UserStateService } from '@app/infrastructure/core/services/state/user-state.service';
import { StudentCoinService } from '@app/infrastructure/core/services/api/student-coin.service';
import { CritterService } from '@app/infrastructure/core/services/api/critter.service';
import { CritterState } from '@app/infrastructure/core/services/state/critter.state';

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SettingsComponent {
  @Input() loggedInUser: IUserDTO = new UserDTO();
  @Input() navLinks: INavigation[] = [];
  @Input() profileLinks: INavigation[] = [];
  @Input() permissionCheck: IPermissionCheck = new PermissionCheck();

  public catImg = `assets/img/cat.png`;
  public waffleMenu = `assets/img/waffle_menu.png`;
  public get isStudent(): boolean {
    return !!this.loggedInUser.student;
  }

  public get avatar(): string {
    const user = this.userState.userSession$.getValue();
    if (user?.student?.critter?.avatarImage?.signedUrl) {
      return user.student.critter.avatarImage.signedUrl;
    }

    return 'assets/img/potato.png';
  }

  public get critter(): Critter {
    const user = this.userState.userSession$.getValue();
    if (user?.student?.critter) {
      return user.student.critter;
    }
    return null;
  }

  public get username(): string {
    if (this.loggedInUser?.student) {
      return this.loggedInUser?.student?.studentName;
    } else if (this.loggedInUser?.teacher) {
      return this.loggedInUser?.teacher.name;
    }
    return `${this.loggedInUser.firstName} ${this.loggedInUser.lastName}`;
  }

  private purchasedCritter: Critter = null;

  constructor(
    private authService: AuthService,
    private modalService: ModalService,
    private router: Router,
    private studentCritterService: StudentCritterService,
    private studentCoinService: StudentCoinService,
    private userState: UserStateService,
    private critterService: CritterService,
    private critterState: CritterState,
  ) {}

  public ngOnInit(): void {
    this.userState.userSession$.subscribe((userSession) => {});
  }

  public openAvatarDialog(): void {
    this.modalService
      .openAvatarDialog()
      .pipe(
        switchMap((isConfirmed: boolean) => {
          if (isConfirmed) {
            this.studentCoinService.coinPurse =
              this.studentCoinService.coinPurse - 5;
            if (this.critter) {
              return this.studentCritterService.updateStudentCritter(
                this.critter.id,
              );
            }
          }
          return of(null);
        }),
      )
      .subscribe((critter: Critter) => {});
  }

  public openGachaDialog(): void {
    this.modalService
      .openGachaDialog()
      .pipe(
        switchMap((isConfirmed: boolean) => {
          if (isConfirmed) {
            return this.studentCritterService.addCritterToCollection();
          }
          return of(null);
        }),
        switchMap((critter: Critter) => {
          if (critter) {
            const userSession = this.userState.userSession;
            userSession.student.coinPurse -= 20;
            this.studentCoinService.coinPurse = userSession.student.coinPurse;
            this.critterState.addCritterToCollection(critter);
            this.purchasedCritter = critter;
            this.userState.setUserSession(userSession);
            return this.modalService.openCritterDialog(critter);
          }
          return of(false);
        }),
        switchMap((isConfirmed: boolean) => {
          if (isConfirmed && this.purchasedCritter) {
            return this.studentCritterService.updateStudentCritter(
              this.purchasedCritter.id,
              { ignoreFee: true },
            );
          }
          return of(null);
        }),
      )
      .subscribe((critter: Critter) => {
        if (critter) {
          this.userState.setUserAvatar(critter, false);
        }
      });
  }

  public openConfirmDialog(): void {
    const dialogConfig = new ConfirmDialogConfig({
      title: 'This will end your login session.',
      submit: new SaveCancelButtonConfig({
        save: 'Log Out',
      }),
    });
    this.modalService
      .openConfirmDialog(dialogConfig)
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.signOut();
        }
      });
  }

  public signOut(): void {
    this.authService.logout().subscribe();
    this.router.navigateByUrl(`/${ROUTES.auth.root}`);
  }
}
